import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import type { ReactNode } from "react";
import { useAuth } from "./auth/AuthContext";
import { AppShell } from "./components/AppShell";
import { LoginPage } from "./pages/LoginPage";
import { SignupPage } from "./pages/SignupPage";
import { ForgotPasswordPage } from "./pages/ForgotPasswordPage";
import { ResetPasswordPage } from "./pages/ResetPasswordPage";
import { AcceptInvitationPage } from "./pages/AcceptInvitationPage";
import { DashboardPage } from "./pages/DashboardPage";
import { BrandPickerPage } from "./pages/BrandPickerPage";
import { CreateBrandPage } from "./pages/CreateBrandPage";
import { PeoplePage } from "./pages/PeoplePage";
import { SettingsPage } from "./pages/SettingsPage";
import { GroundTruthPage } from "./pages/GroundTruthPage";
import { PromptsPage } from "./pages/PromptsPage";
import { DriftPage } from "./pages/DriftPage";
import { CapturesPage } from "./pages/CapturesPage";
import { ReportsPage } from "./pages/ReportsPage";
import { ActionsPage } from "./pages/ActionsPage";

function FullPageLoading() {
  return (
    <div style={{ display: "grid", placeItems: "center", minHeight: "100%" }}>
      <div style={{ width: 280 }}>
        <div
          className="skeleton"
          style={{ height: 12, marginBottom: "var(--space-3)" }}
        />
        <div className="skeleton" style={{ height: 12, width: "70%" }} />
        <span className="visually-hidden">Loading</span>
      </div>
    </div>
  );
}

function RequireAuth({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) return <FullPageLoading />;
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  return <>{children}</>;
}

/**
 * FR-AUTH-05 — a user with several Brand grants and no remembered choice gets
 * the picker; a user with exactly one is taken straight to it. The picker is
 * never shown to someone who has no decision to make.
 */
function RequireBrandContext({ children }: { children: ReactNode }) {
  const { user, activeBrand } = useAuth();
  const location = useLocation();

  const needsPicker =
    (user?.brandGrants.length ?? 0) > 1 &&
    !activeBrand &&
    location.pathname !== "/select-brand";

  if (needsPicker) return <Navigate to="/select-brand" replace />;
  return <>{children}</>;
}

function RedirectIfAuthed({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <FullPageLoading />;
  if (user) return <Navigate to="/" replace />;
  return <>{children}</>;
}

export function App() {
  return (
    <Routes>
      <Route
        path="/login"
        element={
          <RedirectIfAuthed>
            <LoginPage />
          </RedirectIfAuthed>
        }
      />
      {/* Invite-only by default (DECISIONS.md AUTH-004). The route stays so a
          deployment that turns ALLOW_SELF_SIGNUP on needs no code change — the
          page itself refuses when the server says signup is closed. */}
      <Route
        path="/signup"
        element={
          <RedirectIfAuthed>
            <SignupPage />
          </RedirectIfAuthed>
        }
      />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      {/* Unauthenticated by necessity — the invitee may have no account yet. */}
      <Route path="/accept-invitation" element={<AcceptInvitationPage />} />

      <Route
        path="/select-brand"
        element={
          <RequireAuth>
            <BrandPickerPage />
          </RequireAuth>
        }
      />

      <Route
        element={
          <RequireAuth>
            <RequireBrandContext>
              <AppShell />
            </RequireBrandContext>
          </RequireAuth>
        }
      >
        <Route path="/" element={<DashboardPage />} />
        <Route path="/ground-truth" element={<GroundTruthPage />} />
        <Route path="/prompts" element={<PromptsPage />} />
        <Route path="/captures" element={<CapturesPage />} />
        <Route path="/drift" element={<DriftPage />} />
        <Route path="/actions" element={<ActionsPage />} />
        <Route path="/reports" element={<ReportsPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/settings/people" element={<PeoplePage />} />
        <Route path="/settings/brands/new" element={<CreateBrandPage />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
